/**
 * @opal/layouts shim — layout wrapper components.
 */
import React, { type ReactNode, type HTMLAttributes } from "react";
import type { IconFunctionComponent } from "./types";

interface ContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  /**
   * icon/title/description/sizePreset/variant — real Opal `Content` routes
   * these through ContentXl/Lg/Md/Sm based on sizePreset+variant (see
   * ui/web/CLAUDE.md "Opal Layouts"). This shim doesn't implement the full
   * routing matrix, but DOES render icon/title/description (previously
   * silently swallowed into `...rest` and spread onto the DOM `<div>` —
   * CR-017 SOL-017 §1.3 bug fix). sizePreset/variant are accepted (so they
   * don't leak onto the DOM as invalid attributes) but not used for layout.
   */
  icon?: IconFunctionComponent;
  title?: string;
  description?: string;
  sizePreset?: string;
  variant?: string;
}

export function Content({
  children,
  className = "",
  icon: Icon,
  title,
  description,
  sizePreset = "main-ui",
  variant = "section",
  ...rest
}: ContentProps) {
  const isHeadline = sizePreset === "headline";
  const isSection = sizePreset === "section";
  const isMainContent = sizePreset === "main-content";
  const isSecondary = sizePreset === "secondary";

  const titleCls = isHeadline
    ? "font-heading-h2 text-text-04"
    : isSection
      ? "font-heading-h3 text-text-04"
      : isMainContent
        ? "font-main-content-body font-medium text-text-04"
        : isSecondary
          ? "font-secondary-body font-medium text-text-04"
          : "font-main-ui-body text-text-04";

  const descCls = isSecondary || sizePreset === "main-ui"
    ? "font-secondary-body text-text-03"
    : "font-main-ui-muted text-text-03";

  const iconCls = isHeadline
    ? "h-6 w-6 shrink-0 text-text-01"
    : isSection || isMainContent
      ? "h-5 w-5 shrink-0 text-text-01"
      : isSecondary
        ? "h-3.5 w-3.5 shrink-0 text-text-02"
        : "h-4 w-4 shrink-0 text-text-02";

  return (
    <div className={`flex flex-col ${isHeadline ? "gap-2" : "gap-0.5"} ${className}`} {...rest}>
      {(Icon || title || description) && (
        <div className="flex flex-col gap-0.5">
          {/* Icon + title on same row */}
          {(Icon || title) && (
            <div className="flex items-center gap-1.5">
              {Icon && <Icon className={iconCls} />}
              {title && (
                <div className={titleCls}>{title}</div>
              )}
            </div>
          )}
          {description && (
            <div className={descCls}>{description}</div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

interface ContentActionProps extends ContentProps {
  /** Right-aligned actions slot (buttons, badges, etc.) — see ContentAction docs in ui/web/CLAUDE.md. */
  rightChildren?: ReactNode;
  padding?: string;
  prominence?: string;
}

export function ContentAction({
  children,
  className = "",
  icon: Icon,
  title,
  description,
  sizePreset: _sizePreset,
  variant: _variant,
  rightChildren,
  padding: _padding,
  prominence: _prominence,
  ...rest
}: ContentActionProps) {
  return (
    <div className={`flex items-center gap-2 justify-between ${className}`} {...rest}>
      <div className="flex items-center gap-3 min-w-0">
        {Icon && <Icon className="h-5 w-5 shrink-0 text-text-01" />}
        {(title || description) && (
          <div className="flex flex-col min-w-0">
            {title && (
              <div className="font-main-ui-action text-text-01 truncate">{title}</div>
            )}
            {description && (
              <div className="font-secondary-body text-text-03 truncate">{description}</div>
            )}
          </div>
        )}
        {children}
      </div>
      {rightChildren}
    </div>
  );
}

export interface InputLayoutProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  children?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  suffix?: ReactNode;
  withLabel?: string | boolean;
  disabled?: boolean;
  center?: boolean;
}

export function InputVertical({
  children,
  className = "",
  title,
  description,
  suffix,
  withLabel,
  disabled,
  ...rest
}: InputLayoutProps) {
  return (
    <div
      className={`flex flex-col gap-1.5 w-full ${
        disabled ? "opacity-60 pointer-events-none" : ""
      } ${className}`}
      {...rest}
    >
      {(title || description || suffix) && (
        <div className="flex flex-col gap-0.5">
          {(title || suffix) && (
            <div className="flex items-center gap-2">
              {title &&
                (typeof withLabel === "string" ? (
                  <label
                    htmlFor={withLabel}
                    className="font-main-ui-body font-medium text-text-04 cursor-pointer"
                  >
                    {title}
                  </label>
                ) : (
                  <div className="font-main-ui-body font-medium text-text-04">
                    {title}
                  </div>
                ))}
              {suffix && (
                <span className="font-secondary-body text-text-03">
                  {typeof suffix === "string" && !suffix.startsWith("(")
                    ? `(${suffix})`
                    : suffix}
                </span>
              )}
            </div>
          )}
          {description && (
            <div className="font-secondary-body text-text-03 leading-relaxed">
              {description}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export function InputHorizontal({
  children,
  className = "",
  title,
  description,
  suffix,
  withLabel,
  disabled,
  center,
  ...rest
}: InputLayoutProps) {
  return (
    <div
      className={`flex ${
        center ? "items-center" : "items-start"
      } justify-between gap-4 w-full ${
        disabled ? "opacity-60 pointer-events-none" : ""
      } ${className}`}
      {...rest}
    >
      {title || description || suffix ? (
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          {(title || suffix) && (
            <div className="flex items-center gap-2">
              {title &&
                (typeof withLabel === "string" ? (
                  <label
                    htmlFor={withLabel}
                    className="font-main-ui-body font-medium text-text-04 cursor-pointer"
                  >
                    {title}
                  </label>
                ) : (
                  <div className="font-main-ui-body font-medium text-text-04">
                    {title}
                  </div>
                ))}
              {suffix && (
                <span className="font-secondary-body text-text-03">
                  {typeof suffix === "string" && !suffix.startsWith("(")
                    ? `(${suffix})`
                    : suffix}
                </span>
              )}
            </div>
          )}
          {description && (
            <div className="font-secondary-body text-text-03 leading-relaxed">
              {description}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1" />
      )}
      <div className="shrink-0 flex items-center">{children}</div>
    </div>
  );
}

export function InputDivider({ className = "" }: { className?: string }) {
  return <div className={`border-t border-divider-01 my-4 ${className}`} />;
}

interface IllustrationContentProps
  extends Omit<ContentProps, "icon"> {
  /**
   * `illustration` (not `icon`) — real Opal `IllustrationContent` prop name
   * (ui/web/CLAUDE.md "IllustrationContent"). Same shim bug as `Content`
   * (CR-017 SOL-017 §1.3): illustration/title/description were previously
   * swallowed into `...rest` and spread onto the DOM `<div>`, so empty
   * states across the app (GroupsPage, AgentsTable, UsersTable, ...)
   * silently rendered blank. Fixed here alongside `Content`.
   */
  illustration?: IconFunctionComponent;
}

export function IllustrationContent({
  children,
  className = "",
  illustration: Illustration,
  title,
  description,
  ...rest
}: IllustrationContentProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 py-16 text-center ${className}`}
      {...rest}
    >
      {Illustration && <Illustration className="h-[7.5rem] w-[7.5rem]" />}
      {title && <div className="font-heading-h3 text-text-01">{title}</div>}
      {description && (
        <div className="font-secondary-body text-text-03">{description}</div>
      )}
      {children}
    </div>
  );
}

interface LabelProps extends HTMLAttributes<HTMLLabelElement> { children?: ReactNode; required?: boolean; }
export function Label({ children, required, className = "", ...rest }: LabelProps) {
  return (
    <label className={`text-sm font-medium text-gray-700 ${className}`} {...rest}>
      {children}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>
  );
}

export function InputPadder({ children, className = "" }: ContentProps) {
  return <div className={`pl-4 ${className}`}>{children}</div>;
}

export function InputErrorText({ children, className = "" }: ContentProps) {
  return <p className={`text-sm text-red-600 mt-1 ${className}`}>{children}</p>;
}

function CardBase({ children, className = "" }: ContentProps) {
  return <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm ${className}`}>{children}</div>;
}

// CR-024 fix: every real consumer of `Card.Header` (WebSearchPage/index.tsx,
// ImageGenerationPage/ImageGenerationContent.tsx, CodeInterpreterPage/index.tsx,
// sections/admin/ProviderCard.tsx — the last one used by VoiceConfigurationPage,
// this CR's own admin page) renders it as a standalone header row directly
// inside a `SelectCard`, NOT nested inside a `<Card>` wrapper — same
// compound-static-missing root cause as the Hoverable/Interactive bugs above
// (see their comments in core.tsx): this shim only exported a plain `Card`
// function with no `.Header`, so `<Card.Header>` was `<undefined>` at render
// time, crashing every one of those pages with React error #130. Adding a
// minimal, non-EE-specific `Header` here fixes rendering for all of them.
interface CardHeaderProps {
  headerChildren?: ReactNode;
  topRightChildren?: ReactNode;
  bottomRightChildren?: ReactNode;
  className?: string;
}

function CardHeader({
  headerChildren,
  topRightChildren,
  bottomRightChildren,
  className = "",
}: CardHeaderProps) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">{headerChildren}</div>
        {topRightChildren}
      </div>
      {bottomRightChildren && (
        <div className="flex items-center justify-end">{bottomRightChildren}</div>
      )}
    </div>
  );
}

export const Card = Object.assign(CardBase, { Header: CardHeader });
